import React, { useState, useEffect } from 'react';
import Layout from './components/Layout';
import LandingPage from './components/LandingPage';
import Login from './components/Login';
import Register from './components/Register';
import ForgotPassword from './components/ForgotPassword';
import ChangePassword from './components/ChangePassword';
import Dashboard from './components/Dashboard';
import ItineraryForm from './components/ItineraryForm';
import ItineraryDisplay from './components/ItineraryDisplay';
import UserProfile from './components/UserProfile';
import Account from './components/Account';
import TravelPoints from './components/TravelPoints';
import AdSense from './components/AdSense';
import TermsOfService from './components/TermsOfService';
import PrivacyPolicy from './components/PrivacyPolicy';
import Modal from './components/Modal';
import Pricing from './components/Pricing';
import ContactUs from './components/ContactUs';
import AffiliateRequest from './components/AffiliateRequest';
import RewardsPage from './components/RewardsPage';
import AboutUs from './components/AboutUs';
import FAQ from './components/FAQ';
import HowTo from './components/HowTo';
import Videos from './components/Videos';
import Analytics from './components/Analytics';
import System from './components/System';
import FeedbackReview from './components/FeedbackReview';
import DockerMonitoring from './components/DockerMonitoring';
import TestRunner from './components/TestRunner';
import OfflineIndicator from './components/OfflineIndicator';
import BlogList from './components/BlogList';
import BlogArticle from './components/BlogArticle';
import BlogUnsubscribe from './components/BlogUnsubscribe';
import ItineraryProgress from './components/ItineraryProgress';
import SampleItinerary from './components/demo/SampleItinerary';
import MagicLinkLanding from './components/MagicLinkLanding';
import PublicTripView from './components/PublicTripView';
import TravelGuides from './components/TravelGuides';
import Destinations from './components/Destinations';
import ToolsPage from './components/ToolsPage';
import TryoutPage from './components/TryoutPage';
import ConfiguredItineraries from './components/ConfiguredItineraries';
import ConfiguredItineraryDetail from './components/ConfiguredItineraryDetail';
import CookieConsent from './components/CookieConsent';
import RetargetingPixels from './components/RetargetingPixels';
import { ToastContainer, useToast } from './components/Toast';
import { login, googleAuth, logout, createItinerary, updateItinerary, getItinerary, acceptShare, acceptMemberInvite, getCurrentUser, verifyCheckoutSession } from './api/client';
import { FaTimes } from 'react-icons/fa';
import ReactDOM from 'react-dom';
import { trackPageView } from './utils/tracker';
import { getPendingTripContext, clearPendingTripContext } from './utils/tripContext';
import { getDistanceUnit } from './utils/distanceUtils';
import { VoiceProvider } from './components/voice/VoiceProvider';

// The API requires at least one interest, and a visitor who arrived from an
// article never gave us any. Kept deliberately neutral: this is a starting
// point the traveller regenerates with their own interests, not a guess at what
// they like.
const DEFAULT_REPLAY_INTERESTS = ['travel', 'exploration'];

// Feature flags
const REWARDS_ENABLED = process.env.REACT_APP_REWARDS_ENABLED === 'true';

// Map URL paths to app state — single source of truth for all route handling.
// Adding a route here is all that's needed; both direct navigation and in-app
// links will work automatically.
const PATH_TO_VIEW = {
  '/': 'landing',
  '/pricing': 'pricing',
  '/contact': 'contact',
  '/about': 'about',
  '/faq': 'faq',
  '/howto': 'howto',
  '/videos': 'videos',
  '/affiliate': 'affiliate',
  '/blog': 'blog',
  '/rewards': 'rewards',
  '/guides': 'guides',
  '/destinations': 'destinations',
  '/tools': 'tools',
  '/tryout': 'tryout',
  '/privacy': 'privacy',
  '/terms': 'terms',
  '/configured-itineraries': 'configured-itineraries',
};
const AUTH_PATHS = { '/login': false, '/register': true }; // value = showRegister

function getInitialStateFromURL() {
  const path = window.location.pathname;
  if (path in AUTH_PATHS) {
    return { view: 'dashboard', showAuth: true, showRegister: AUTH_PATHS[path], sampleSlug: null, blogSlug: null, unsubscribeToken: null };
  }
  if (path in PATH_TO_VIEW) {
    // Configured itineraries require login — auto-show auth form when not yet authenticated.
    // (If already logged in, App.js bypasses the auth form anyway and renders the view directly.)
    const needsAuth = path === '/configured-itineraries';
    return { view: PATH_TO_VIEW[path], showAuth: needsAuth, showRegister: false, sampleSlug: null, blogSlug: null, unsubscribeToken: null };
  }
  // /trip-cost is the cost estimator's short URL, worth having because people
  // link to and type it. It is an alias, not a second page: rewrite it to the
  // canonical /tools/trip-cost so the two never compete in search results.
  if (path === '/trip-cost') {
    window.history.replaceState({}, '', '/tools/trip-cost');
    return { view: 'tools', showAuth: false, showRegister: false, sampleSlug: null, blogSlug: null, unsubscribeToken: null, toolSlug: 'trip-cost' };
  }
  // Individual tool routes: /tools/dark-sky. Each tool is its own indexable
  // page, so a direct hit has to open that tool, not the index.
  const toolMatch = path.match(/^\/tools\/([a-z0-9-]+)$/);
  if (toolMatch) {
    return { view: 'tools', showAuth: false, showRegister: false, sampleSlug: null, blogSlug: null, unsubscribeToken: null, toolSlug: toolMatch[1] };
  }
  // Blog article routes: /blog/some-slug
  if (path.startsWith('/blog/')) {
    const slug = path.slice(6); // remove '/blog/'
    return { view: 'blog-article', showAuth: false, showRegister: false, sampleSlug: null, blogSlug: slug, unsubscribeToken: null };
  }
  // Handle /unsubscribe/:token paths
  const unsubscribeMatch = path.match(/^\/unsubscribe\/([a-zA-Z0-9_-]+)$/);
  if (unsubscribeMatch) {
    return { view: 'unsubscribe', showAuth: false, showRegister: false, sampleSlug: null, blogSlug: null, unsubscribeToken: unsubscribeMatch[1] };
  }
  // The emailed sign-in link for an email-first account. Handled before
  // anything else that could bounce an unauthenticated visitor, because at this
  // point they ARE the credential.
  if (path === '/auth/magic') {
    return { view: 'magic-link', showAuth: false, showRegister: false, sampleSlug: null, blogSlug: null, unsubscribeToken: null };
  }
  // Public read-only trip: /trip/public/:token
  const publicTripMatch = path.match(/^\/trip\/public\/([A-Za-z0-9_-]+)$/);
  if (publicTripMatch) {
    return { view: 'public-trip', showAuth: false, showRegister: false, sampleSlug: null, blogSlug: null, unsubscribeToken: null, publicTripToken: publicTripMatch[1] };
  }
  // Handle /sample/:slug paths
  const sampleMatch = path.match(/^\/sample\/([a-z0-9-]+)$/);
  if (sampleMatch) {
    return { view: 'sample', showAuth: false, showRegister: false, sampleSlug: sampleMatch[1], blogSlug: null, unsubscribeToken: null };
  }
  // Handle /configured-itineraries/:slug paths (detail view) — requires login.
  const configuredMatch = path.match(/^\/configured-itineraries\/([a-z0-9-]+)$/);
  if (configuredMatch) {
    return { view: 'configured-itinerary-detail', showAuth: true, showRegister: false, sampleSlug: null, blogSlug: null, unsubscribeToken: null, configuredSlug: configuredMatch[1] };
  }
  // Handle /itinerary/:id deep links (e.g. "View Full Advisories" links in notification
  // emails) — requires login. The itinerary is loaded by request_id once authenticated.
  const itineraryMatch = path.match(/^\/itinerary\/([a-zA-Z0-9_-]+)$/);
  if (itineraryMatch) {
    return { view: 'view-itinerary', showAuth: true, showRegister: false, sampleSlug: null, blogSlug: null, unsubscribeToken: null, itineraryId: itineraryMatch[1] };
  }
  return { view: 'dashboard', showAuth: false, showRegister: false, sampleSlug: null, blogSlug: null, unsubscribeToken: null };
}

function App() {
  const initialState = getInitialStateFromURL();
  const [user, setUser] = useState(null);
  const [showRegister, setShowRegister] = useState(initialState.showRegister);
  const [sampleSlug, setSampleSlug] = useState(initialState.sampleSlug);
  const [publicTripToken, setPublicTripToken] = useState(initialState.publicTripToken || null);
  const [blogSlug, setBlogSlug] = useState(initialState.blogSlug);
  const [unsubscribeToken, setUnsubscribeToken] = useState(initialState.unsubscribeToken);
  // Destination carried from a blog article into the tryout preview
  const [planDestination, setPlanDestination] = useState('');
  // Which of the eight free tools is open, mirrored in the URL as /tools/:id
  const [toolSlug, setToolSlug] = useState(initialState.toolSlug || null);
  const [configuredSlug, setConfiguredSlug] = useState(initialState.configuredSlug || null);
  const [deepLinkItineraryId, setDeepLinkItineraryId] = useState(initialState.itineraryId || null);
  const { toasts, removeToast, showSuccess, showError, showWarning, showUpgrade } = useToast();
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false); // For forced password reset
  const [showAuthForm, setShowAuthForm] = useState(initialState.showAuth); // Show login/register form instead of landing page
  const [currentView, setCurrentView] = useState(initialState.view); // 'dashboard', 'new-itinerary', 'view-itinerary', 'profile', 'account', 'pricing', 'contact'
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [itinerary, setItinerary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [requestId, setRequestId] = useState(null);
  const [subscriptionLimitError, setSubscriptionLimitError] = useState(null);
  const [shareToken, setShareToken] = useState(null);
  const [inviteToken, setInviteToken] = useState(null);
  const [formInitialData, setFormInitialData] = useState(null);
  const [viewingAsOwner, setViewingAsOwner] = useState(true);
  const [loadingItineraryId, setLoadingItineraryId] = useState(null); // Track which itinerary is being loaded
  const [cookieConsent, setCookieConsent] = useState(null); // Cookie consent preferences

  // Scroll to top and track page view when view changes
  useEffect(() => {
    window.scrollTo(0, 0);
    // Skip tracking for blog-article - handled by BlogArticle component directly
    if (currentView === 'blog-article') return;
    // Skip tracking for sample view without slug (transient state)
    if (currentView === 'sample' && !sampleSlug) return;

    // Protected pages - only track if user is logged in
    const protectedViews = [
      'dashboard', 'new-itinerary', 'view-itinerary', 'profile', 'account',
      'progress', 'analytics', 'feedback-review', 'docker-monitoring',
      'travel-points', 'system'
    ];
    if (protectedViews.includes(currentView) && !user) return;

    // Compute correct page path for analytics tracking
    let pagePath;
    if (currentView === 'landing') {
      pagePath = '/';
    } else if (currentView === 'sample' && sampleSlug) {
      pagePath = `/sample/${sampleSlug}`;
    } else {
      pagePath = '/' + currentView;
    }
    trackPageView(pagePath, user);
  }, [currentView, sampleSlug, user]);

  // Back / forward. Views are pushed onto history (a tool, a blog article, a
  // sample), but nothing was listening for the pop, so going back changed the
  // address bar and left the previous screen on display. Re-derive the view
  // from the URL — PATH_TO_VIEW above is already the single source of truth.
  useEffect(() => {
    const handlePopState = () => {
      const next = getInitialStateFromURL();
      setToolSlug(next.toolSlug || null);
      setBlogSlug(next.blogSlug);
      setSampleSlug(next.sampleSlug);
      setPublicTripToken(next.publicTripToken || null);
      setUnsubscribeToken(next.unsubscribeToken);
      setConfiguredSlug(next.configuredSlug || null);
      setCurrentView(next.view);
      // Only an anonymous visitor can be sent back to a login/register URL.
      if (!user) {
        setShowAuthForm(next.showAuth);
        setShowRegister(next.showRegister);
      }
      window.scrollTo(0, 0);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [user]);

  // Check for stored user and share token on mount
  useEffect(() => {
    // Check for share token in URL
    const path = window.location.pathname;
    const shareMatch = path.match(/\/accept-share\/([^/]+)/);
    if (shareMatch) {
      const token = shareMatch[1];
      setShareToken(token);
      // Clear the URL without reloading the page
      window.history.replaceState({}, '', '/');
    }

    // Check for member invite token in URL
    const inviteMatch = path.match(/\/accept-invite\/([^/]+)/);
    if (inviteMatch) {
      const token = inviteMatch[1];
      setInviteToken(token);
      // Clear the URL without reloading the page
      window.history.replaceState({}, '', '/');
    }

    // Check for subscription status in URL params
    const urlParams = new URLSearchParams(window.location.search);
    const subscriptionStatus = urlParams.get('subscription');
    if (subscriptionStatus === 'success') {
      // Verify checkout session with backend, then refresh user data
      const verifyAndRefresh = async () => {
        try {
          const sessionId = sessionStorage.getItem('stripe_session_id');
          if (sessionId) {
            await verifyCheckoutSession(sessionId);
            sessionStorage.removeItem('stripe_session_id');
          }
          const userData = await getCurrentUser();
          setUser(userData);
          localStorage.setItem('user', JSON.stringify(userData));
          setTimeout(() => {
            const event = new CustomEvent('show-toast', {
              detail: { message: 'Your subscription is now active. Welcome!', type: 'success' }
            });
            window.dispatchEvent(event);
          }, 500);
        } catch (error) {
          console.error('Error verifying subscription:', error);
        }
      };
      verifyAndRefresh();
      window.history.replaceState({}, '', '/');
    } else if (subscriptionStatus === 'canceled') {
      window.history.replaceState({}, '', '/');
    }

    // Also handle PAYG success
    const paygStatus = urlParams.get('payg');
    if (paygStatus === 'success') {
      const verifyPayg = async () => {
        try {
          const sessionId = sessionStorage.getItem('stripe_session_id');
          if (sessionId) {
            await verifyCheckoutSession(sessionId);
            sessionStorage.removeItem('stripe_session_id');
          }
          const userData = await getCurrentUser();
          setUser(userData);
          localStorage.setItem('user', JSON.stringify(userData));
          setTimeout(() => {
            const event = new CustomEvent('show-toast', {
              detail: { message: 'Trip credit purchased successfully!', type: 'success' }
            });
            window.dispatchEvent(event);
          }, 500);
        } catch (error) {
          console.error('Error verifying PAYG purchase:', error);
        }
      };
      verifyPayg();
      window.history.replaceState({}, '', '/');
    }

    const storedUser = localStorage.getItem('user');
    const accessToken = localStorage.getItem('access_token');
    if (storedUser && accessToken) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (error) {
        console.error('Error parsing stored user:', error);
        localStorage.removeItem('user');
        localStorage.removeItem('access_token');
      }
    }
  }, []);

  // Resolve /itinerary/:id deep links (e.g. "View Full Advisories" in advisory emails)
  // once the user is authenticated: load the itinerary by request_id and show it.
  // Until the user logs in, the auth form is shown (showAuth was set from the URL).
  useEffect(() => {
    if (user && deepLinkItineraryId) {
      const id = deepLinkItineraryId;
      setDeepLinkItineraryId(null);
      // Clean the URL so refresh/back doesn't re-trigger the deep link.
      window.history.replaceState({}, '', '/');
      handleViewItinerary(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, deepLinkItineraryId]);

  // Poll for itinerary updates - optimized with faster polling
  useEffect(() => {
    let pollInterval;

    if (requestId && (itinerary?.status === 'pending' || itinerary?.status === 'processing')) {
      // Poll every 1 second for faster response
      pollInterval = setInterval(async () => {
        try {
          const data = await getItinerary(requestId);
          setItinerary(data);

          if (data.status === 'completed' || data.status === 'failed') {
            setLoading(false);
            clearInterval(pollInterval);
            // Auto-transition from progress to view-itinerary when completed
            if (data.status === 'completed' && currentView === 'progress') {
              // Small delay to show the completion animation
              setTimeout(() => {
                setCurrentView('view-itinerary');
              }, 2000);
            }
          }
        } catch (error) {
          console.error('Error polling itinerary:', error);
        }
      }, 1000); // Reduced from 3000ms to 1000ms for faster updates
    }

    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [requestId, itinerary?.status, currentView]);

  // Surface a renew prompt and route to /pricing whenever the API rejects a
  // create/regenerate call with subscription_inactive.
  useEffect(() => {
    const handler = (event) => {
      const detail = event.detail || {};
      const message = detail.message || 'Your subscription is not active. Renew to plan new trips.';
      showUpgrade(message);
      setCurrentView('pricing');
    };
    window.addEventListener('subscription-inactive', handler);
    return () => window.removeEventListener('subscription-inactive', handler);
  }, [showUpgrade]);

  // Everything that has to happen after a successful authentication, whatever
  // form it took: bind a pending share, bind a pending group invite, and resume
  // a checkout the user started on the pricing page before they had an account.
  //
  // Login, registration and Google sign-in all funnel through here. Keeping it
  // in one place is what stops the Google path from quietly skipping a step.
  const completePostAuth = async () => {
    // If there's a pending share token, accept it
    if (shareToken) {
      try {
        await acceptShare(shareToken);
        setShareToken(null);
        showSuccess('Itinerary successfully added to your account!');
      } catch (error) {
        console.error('Error accepting share:', error);
        showError('Failed to add shared itinerary. It may not be for your email address.');
      }
    }

    // If there's a pending member invite token, accept it
    if (inviteToken) {
      try {
        await acceptMemberInvite(inviteToken);
        setInviteToken(null);
        showSuccess('You have been added to the travel group!');
      } catch (error) {
        console.error('Error accepting invite:', error);
        showError('Failed to join the group. The invitation may not be for your email address.');
      }
    }

    // Check for pending plan from pricing page and auto-checkout
    const pendingPlanStr = sessionStorage.getItem('pending_plan');
    if (pendingPlanStr) {
      try {
        const pendingPlan = JSON.parse(pendingPlanStr);
        sessionStorage.removeItem('pending_plan'); // Clear it immediately

        // Import checkout functions dynamically to avoid circular deps
        const { createCheckoutSession, createPaygCheckout } = await import('./api/client');

        if (pendingPlan.type === 'payg') {
          // PAYG checkout
          const result = await createPaygCheckout(pendingPlan.planType);
          if (result.checkout_url) {
            if (result.session_id) {
              sessionStorage.setItem('stripe_session_id', result.session_id);
            }
            window.location.href = result.checkout_url;
            return; // Don't navigate anywhere, we're redirecting to Stripe
          }
        } else if (pendingPlan.type === 'subscription') {
          // Subscription checkout
          const result = await createCheckoutSession(pendingPlan.billingCycle, pendingPlan.tierId);

          // Check if a 100% promotion was applied directly (no Stripe redirect)
          if (result.upgraded) {
            showSuccess(result.message || 'Free promotion applied successfully!');
            setCurrentView('dashboard');
            return;
          }

          if (result.checkout_url) {
            if (result.session_id) {
              sessionStorage.setItem('stripe_session_id', result.session_id);
            }
            window.location.href = result.checkout_url;
            return; // Don't navigate anywhere, we're redirecting to Stripe
          }
        }
      } catch (error) {
        console.error('Error processing pending plan checkout:', error);
        showError('Failed to start checkout. Please try again from the pricing page.');
      }
    }

    // Replay the trip they were already researching. Somewhere on an article,
    // a sample or the preview they said where they were going and when — that
    // is the whole itinerary brief, and it should not have to be typed again
    // just because they have now signed up.
    const pending = getPendingTripContext();
    if (pending?.destination) {
      // Read the context out before clearing, since both paths below need it.
      const { destination, origin, startDate, endDate } = pending;
      clearPendingTripContext();

      // Where they told us all four fields the generator needs, skip the form
      // entirely and build the trip. This is the payoff for the date pill: the
      // visitor answered "where" by reading and "when" on a booking block, so
      // there is nothing left to ask.
      if (destination && origin && startDate && endDate) {
        handleSubmitItinerary({
          start_location: origin,
          end_location: destination,
          start_date: startDate,
          end_date: endDate,
          // The API requires at least one interest and an anonymous visitor
          // never gave us any. These are the neutral pair the form itself falls
          // back to — deliberately generic rather than invented preferences,
          // and the traveller can regenerate with their own in one step.
          interests: DEFAULT_REPLAY_INTERESTS,
          distance_radius: 50,
          distance_unit: getDistanceUnit(),
        });
        return;
      }

      // Anything less than the full brief goes to the form, prefilled with
      // whatever we do have. Generating from a half-known trip would spend the
      // free itinerary on something they did not ask for.
      handlePlanTripForUser(destination, pending);
      return;
    }

    setCurrentView('dashboard');
  };

  const handleLogin = async (email, password, captchaToken = null, captchaAnswer = null) => {
    const userData = await login(email, password, captchaToken, captchaAnswer);

    // Check if password reset is required
    if (userData.password_reset_required) {
      setShowAuthForm(false);  // Hide login form so change password screen can show
      setShowChangePassword(true);
      return;
    }

    setUser(userData);
    await completePostAuth();
  };

  // Google Sign-In. The server decides whether this is a new account, a
  // returning Google user, or a link onto an existing password account - the
  // response shape is identical either way, so the post-auth handling is the
  // same as for login and registration.
  const handleGoogleAuth = async (credential, opts = {}) => {
    const userData = await googleAuth(credential, { ...opts, client: 'web' });
    setUser(userData);

    if (userData.is_new_user) {
      showSuccess(`Welcome to ItinerarySeek, ${(userData.full_name || '').split(' ')[0]}!`);
    }

    await completePostAuth();
  };

  const handlePasswordChanged = async () => {
    // After password change, fetch user and continue to dashboard
    try {
      const userData = await getCurrentUser();
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      setShowChangePassword(false);
      setCurrentView('dashboard');

      // Handle pending tokens
      if (shareToken) {
        try {
          await acceptShare(shareToken);
          setShareToken(null);
          showSuccess('Itinerary successfully added to your account!');
        } catch (error) {
          console.error('Error accepting share:', error);
        }
      }
      if (inviteToken) {
        try {
          await acceptMemberInvite(inviteToken);
          setInviteToken(null);
          showSuccess('You have been added to the travel group!');
        } catch (error) {
          console.error('Error accepting invite:', error);
        }
      }
    } catch (error) {
      console.error('Error fetching user after password change:', error);
    }
  };

  const handleForgotPassword = () => {
    setShowForgotPassword(true);
  };

  const handleBackToLogin = () => {
    setShowForgotPassword(false);
  };

  const handleLogout = () => {
    logout();
    setUser(null);
    setCurrentView('dashboard');
    setItinerary(null);
    setRequestId(null);
    // Clear per-user form state so a stale request_id can't leak into the
    // next user's "create" and turn it into a regenerate of someone else's trip.
    setFormInitialData(null);
    setSubscriptionLimitError(null);
  };

  const handleNewItinerary = () => {
    setCurrentView('new-itinerary');
    setItinerary(null);
    setRequestId(null);
    setLoading(false);
    setFormInitialData(null);
    setSubscriptionLimitError(null);
  };

  const handleRegenerateFromDashboard = (itineraryData) => {
    // Extract form data from itinerary and include request_id for updating
    const initialData = {
      request_id: itineraryData.request_id, // Include request_id to update existing entry
      start_location: itineraryData.start_location,
      end_location: itineraryData.end_location,
      interests: itineraryData.interests,
      food_preference: itineraryData.food_preference,
      start_date: itineraryData.start_date,
      end_date: itineraryData.end_date,
      transportation_mode: itineraryData.transportation_mode,
      avoid_toll_roads: itineraryData.avoid_toll_roads,
      prefer_toll_roads: itineraryData.prefer_toll_roads,
      scenic_roads: itineraryData.scenic_roads
    };

    setFormInitialData(initialData);
    setCurrentView('new-itinerary');
    setItinerary(null);
    setRequestId(itineraryData.request_id); // Set the request_id to update
    setLoading(false);
  };

  const handleViewItinerary = async (id, isOwner = true) => {
    setLoadingItineraryId(id); // Show loading state immediately
    try {
      const data = await getItinerary(id);
      setItinerary(data);
      setRequestId(id);
      setViewingAsOwner(isOwner);
      setCurrentView('view-itinerary');
    } catch (error) {
      console.error('Error loading itinerary:', error);
      showError('Failed to load itinerary. It may not exist or may belong to another account.');
      // Avoid stranding the user on a blank view-itinerary screen (e.g. when arriving
      // via an /itinerary/:id deep link that fails to load) — return to the dashboard.
      setCurrentView('dashboard');
    } finally {
      setLoadingItineraryId(null); // Clear loading state
    }
  };

  const handleSubmitItinerary = async (formData) => {
    setLoading(true);
    setItinerary(null);
    setSubscriptionLimitError(null); // Clear any previous limit error

    try {
      let data;
      // If request_id exists, update existing itinerary; otherwise create new
      if (formData.request_id) {
        const requestId = formData.request_id;
        // Remove request_id from formData before sending to update endpoint
        const { request_id, ...updateData } = formData;
        data = await updateItinerary(requestId, updateData);
        setRequestId(requestId);
      } else {
        data = await createItinerary(formData);
        setRequestId(data.request_id);
      }

      setItinerary(data);
      // Show progress page while itinerary is being generated
      setCurrentView('progress');
    } catch (error) {
      console.error('Error creating/updating itinerary:', error);
      setLoading(false);
      // Check for specific error messages from the API
      const errorDetail = error.response?.data?.detail;
      if (errorDetail?.error === 'trip_limit_reached') {
        // Reached the plan's itinerary limit (e.g. demo allows 1) — take the user
        // straight to the upgrade page instead of leaving them on the form.
        setSubscriptionLimitError(null);
        showUpgrade(
          errorDetail.message || 'You have reached your trip limit. Upgrade your plan for more itineraries!'
        );
        setCurrentView('pricing');
      } else if (typeof errorDetail === 'string') {
        showError(errorDetail);
      } else {
        showError('Failed to create/update itinerary. Please try again.');
      }
    }
  };

  const handleRegenerateItinerary = async (formData) => {
    // Regenerate is just creating a new itinerary with modified params
    handleSubmitItinerary(formData);
  };

  const handleBackToDashboard = () => {
    setCurrentView('dashboard');
    setItinerary(null);
    setRequestId(null);
    setLoading(false);
    setSubscriptionLimitError(null);
  };

  // Navigation handler that can handle both simple views and itinerary navigation
  const handleNavigate = (view, id = null) => {
    if (view === 'view-itinerary' && id) {
      handleViewItinerary(id);
    } else if (view === 'privacy') {
      setShowPrivacyModal(true);
    } else if (view === 'terms') {
      setShowTermsModal(true);
    } else if (view === 'dashboard') {
      // Clear itinerary state when navigating to dashboard
      setCurrentView('dashboard');
      setItinerary(null);
      setRequestId(null);
      setLoading(false);
      setSubscriptionLimitError(null);
    } else if (view === 'new-itinerary') {
      // Reuse the "New Itinerary" reset so the form opens clean. Otherwise a
      // leftover formInitialData.request_id (e.g. from a prior Regenerate, even
      // across a user switch) makes the next submit a PUT/regenerate instead of
      // a POST/create -> "Regeneration unavailable" for non-owners.
      handleNewItinerary();
    } else {
      if (view === 'account') {
        // Refresh user data so subscription tier is up-to-date
        getCurrentUser().then(userData => {
          setUser(userData);
          localStorage.setItem('user', JSON.stringify(userData));
        }).catch(() => {});
      }
      setCurrentView(view);
    }
  };

  const handleChatModificationApplied = () => {
    // Update itinerary status to 'processing' to trigger polling
    if (itinerary) {
      setItinerary({
        ...itinerary,
        status: 'processing'
      });
      setLoading(true);
      // Navigate to progress page to show regeneration status
      setCurrentView('progress');
    }
  };

  const handleRefreshItinerary = async () => {
    // Refresh itinerary data to get updated totals (e.g., after expense changes)
    if (requestId) {
      try {
        const data = await getItinerary(requestId);
        setItinerary(data);
      } catch (error) {
        console.error('Error refreshing itinerary:', error);
      }
    }
  };

  const handleViewProfile = () => {
    setCurrentView('profile');
  };

  const handleProfileUpdate = (updatedUser) => {
    setUser(updatedUser);
  };

  const handleViewTerms = () => {
    setShowTermsModal(true);
  };

  const handleViewPrivacy = () => {
    setShowPrivacyModal(true);
  };

  const handleViewPricing = () => {
    setCurrentView('pricing');
  };

  const handleViewTools = () => {
    setToolSlug(null);
    setCurrentView('tools');
    window.history.pushState({}, '', '/tools');
    window.scrollTo(0, 0);
  };

  // Open or close one of the free tools. Each has its own URL so it can be
  // linked, shared and indexed on its own.
  const handleSelectTool = (id) => {
    setToolSlug(id || null);
    setCurrentView('tools');
    window.history.pushState({}, '', id ? `/tools/${id}` : '/tools');
  };

  const handleViewTryout = () => {
    setCurrentView('tryout');
    window.history.pushState({}, '', '/tryout');
    window.scrollTo(0, 0);
  };

  // "Plan your own like this" from a published trip. The trip's destination and
  // dates are already stashed as pending context by the view, so the preview —
  // and, after signup, the generated itinerary — start from them.
  const handlePlanTripAnonymous = (destination) => {
    setPlanDestination(destination || '');
    handleViewTryout();
  };

  // The magic link has already established the session; from here it is the
  // same post-authentication work as any other sign-in, which is what replays
  // the trip they were saving when they gave us their address.
  const handleMagicLinkSignedIn = async (signedInUser) => {
    if (signedInUser) setUser(signedInUser);
    await completePostAuth();
  };

  // "Plan this trip" from a blog article — the destination is already stashed as
  // pending trip context by the article, so the preview opens pre-filled.
  const handlePlanTripFromArticle = (destination) => {
    setPlanDestination(destination || '');
    handleViewTryout();
  };

  // Same button for a logged-in reader, who has no reason to see the anonymous
  // preview: open the itinerary form with the article's destination and any
  // dates they picked in the booking block already filled in, still editable.
  const handlePlanTripForUser = (destination, context) => {
    // The caller may already have consumed and cleared the stored context, in
    // which case it hands it in rather than us reading it back.
    const ctx = context || getPendingTripContext() || {};
    setFormInitialData({
      prefill: true,
      end_location: destination || ctx.destination || '',
      start_location: ctx.origin || '',
      start_date: ctx.startDate || '',
      end_date: ctx.endDate || '',
    });
    setItinerary(null);
    setRequestId(null);
    setLoading(false);
    setSubscriptionLimitError(null);
    setCurrentView('new-itinerary');
    window.history.pushState({}, '', '/');
    window.scrollTo(0, 0);
  };

  const handleViewContact = () => {
    setCurrentView('contact');
  };

  const handleViewAffiliate = () => {
    setCurrentView('affiliate');
  };

  const handleViewAbout = () => {
    setCurrentView('about');
  };

  const handleViewFaq = () => {
    setCurrentView('faq');
  };

  const handleViewHowTo = () => {
    setCurrentView('howto');
  };

  const handleViewVideos = () => {
    setCurrentView('videos');
  };

  const handleViewBlog = () => {
    setBlogSlug(null);
    setCurrentView('blog');
  };

  const handleViewGuides = () => {
    setCurrentView('guides');
  };

  const handleViewDestinations = () => {
    setCurrentView('destinations');
  };

  const handleViewConfiguredItineraries = () => {
    setConfiguredSlug(null);
    setCurrentView('configured-itineraries');
    window.history.pushState({}, '', '/configured-itineraries');
  };

  const handleSelectConfiguredItinerary = (slug) => {
    setConfiguredSlug(slug);
    setCurrentView('configured-itinerary-detail');
    window.history.pushState({}, '', `/configured-itineraries/${slug}`);
  };

  const handleBackFromConfiguredDetail = () => {
    setConfiguredSlug(null);
    setCurrentView('configured-itineraries');
    window.history.pushState({}, '', '/configured-itineraries');
  };

  const handleConfiguredCloned = (newRequestId) => {
    // Behave exactly like opening a freshly created itinerary
    setConfiguredSlug(null);
    handleViewItinerary(newRequestId, true);
  };

  const handleViewBlogArticle = (slug) => {
    setBlogSlug(slug);
    setCurrentView('blog-article');
    window.history.pushState({}, '', `/blog/${slug}`);
  };

  const handleViewSample = (slug) => {
    setSampleSlug(slug);
    setCurrentView('sample');
    window.history.pushState({}, '', `/sample/${slug}`);
  };

  const handleBackFromSample = () => {
    setSampleSlug(null);
    setCurrentView('dashboard');
    window.history.pushState({}, '', '/');
  };

  const handleUpgradeSuccess = async () => {
    // Refresh user data after successful upgrade
    try {
      const userData = await getCurrentUser();
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      setCurrentView('dashboard');
    } catch (error) {
      console.error('Error refreshing user data:', error);
    }
  };

  const handleSubscriptionCancelled = async () => {
    // Refresh user data after subscription cancellation
    try {
      const userData = await getCurrentUser();
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      setCurrentView('dashboard');
      showSuccess('Your subscription has been cancelled. You will have access until the end of your billing period.');
    } catch (error) {
      console.error('Error refreshing user data:', error);
    }
  };

  // Helper to go back to landing page from legal pages or auth forms when not authenticated
  const handleBackToLanding = () => {
    setCurrentView('dashboard');
    setShowAuthForm(false);
  };

  const handleShowLogin = () => {
    setShowAuthForm(true);
    setShowRegister(false);
  };

  const handleShowRegister = () => {
    setShowAuthForm(true);
    setShowRegister(true);
  };

  // Not authenticated - show landing page, login/register, or legal pages
  if (!user) {
    // Show auth forms if user clicked login/register (must be checked first!)
    if (showAuthForm || shareToken || inviteToken) {
      return (
        <div className="min-h-screen flex flex-col bg-gradient-to-br from-primary-500 via-primary-600 to-secondary-500">
          {shareToken && (
            <div className="bg-white/20 backdrop-blur-sm text-white py-3 px-4 text-center animate-slide-down">
              <p className="text-sm md:text-base">
                <strong>You've been invited to view a travel itinerary!</strong>
                {' '}Please log in or register to access it.
              </p>
            </div>
          )}
          {inviteToken && (
            <div className="bg-white/20 backdrop-blur-sm text-white py-3 px-4 text-center animate-slide-down">
              <p className="text-sm md:text-base">
                <strong>You've been invited to join a travel group!</strong>
                {' '}Please log in or register to join the trip.
              </p>
            </div>
          )}

          {/* Back to Landing button */}
          {!shareToken && !inviteToken && !showForgotPassword && (
            <div className="absolute top-4 left-4">
              <button
                onClick={handleBackToLanding}
                className="flex items-center space-x-2 text-white/80 hover:text-white font-medium transition-colors"
              >
                <span>←</span>
                <span>Back</span>
              </button>
            </div>
          )}

          <main className="flex-1 flex items-center justify-center px-4 py-8">
            {showForgotPassword ? (
              <ForgotPassword onBackToLogin={handleBackToLogin} />
            ) : showRegister ? (
              <Register
                onGoogleAuth={handleGoogleAuth}
                onSwitchToLogin={() => setShowRegister(false)}
                onNavigate={handleNavigate}
              />
            ) : (
              <Login
                onLogin={handleLogin}
                onGoogleAuth={handleGoogleAuth}
                onSwitchToRegister={() => setShowRegister(true)}
                onForgotPassword={handleForgotPassword}
              />
            )}
          </main>

          {/* Footer links for auth pages */}
          <footer className="py-4 text-center text-white/60 text-sm">
            <button
              onClick={handleViewTerms}
              className="hover:text-white transition-colors mx-2"
            >
              Terms of Service
            </button>
            <span>|</span>
            <button
              onClick={handleViewPrivacy}
              className="hover:text-white transition-colors mx-2"
            >
              Privacy Policy
            </button>
          </footer>

          {/* Terms of Service Modal */}
          <Modal
            isOpen={showTermsModal}
            onClose={() => setShowTermsModal(false)}
            title="Terms of Service"
            size="large"
          >
            <TermsOfService />
          </Modal>

          {/* Privacy Policy Modal */}
          <Modal
            isOpen={showPrivacyModal}
            onClose={() => setShowPrivacyModal(false)}
            title="Privacy Policy"
            size="large"
          >
            <PrivacyPolicy />
          </Modal>
        </div>
      );
    }

    // Show contact page when not logged in
    if (currentView === 'contact') {
      return (
        <div className="min-h-screen flex flex-col bg-neutral-50">
          <ContactUs />
        </div>
      );
    }

    // Show affiliate request page when not logged in
    if (currentView === 'affiliate') {
      return (
        <div className="min-h-screen flex flex-col bg-neutral-50">
          <AffiliateRequest />
        </div>
      );
    }

    // Show about us page when not logged in
    if (currentView === 'about') {
      return (
        <AboutUs onBack={handleBackToLanding} />
      );
    }

    // Show FAQ page when not logged in
    if (currentView === 'faq') {
      return (
        <FAQ onBack={handleBackToLanding} />
      );
    }

    // Show Privacy Policy page
    if (currentView === 'privacy') {
      return (
        <div className="min-h-screen flex flex-col bg-neutral-50">
          <div className="max-w-4xl mx-auto px-4 py-8">
            <PrivacyPolicy />
          </div>
        </div>
      );
    }

    // Show Terms of Service page
    if (currentView === 'terms') {
      return (
        <div className="min-h-screen flex flex-col bg-neutral-50">
          <div className="max-w-4xl mx-auto px-4 py-8">
            <TermsOfService />
          </div>
        </div>
      );
    }

    // Show How To page when not logged in
    if (currentView === 'howto') {
      return (
        <HowTo onBack={handleBackToLanding} onViewVideos={handleViewVideos} onGetStarted={() => { handleShowRegister(); setCurrentView('dashboard'); }} />
      );
    }

    // Show Videos page
    if (currentView === 'videos') {
      return (
        <Videos onBack={handleViewHowTo} />
      );
    }

    if (currentView === 'blog') {
      return (
        <BlogList onBack={handleBackToLanding} onViewArticle={handleViewBlogArticle} onGetStarted={handleShowRegister} onViewTryout={handleViewTryout} />
      );
    }

    if (currentView === 'blog-article' && blogSlug) {
      return (
        <BlogArticle
          slug={blogSlug}
          onBack={handleViewBlog}
          onViewBlog={handleViewBlog}
          onViewTryout={handleViewTryout}
          onViewArticle={handleViewBlogArticle}
          onPlanTrip={handlePlanTripFromArticle}
        />
      );
    }

    if (currentView === 'guides') {
      return (
        <TravelGuides onBack={handleBackToLanding} onViewArticle={handleViewBlogArticle} onViewDestinations={handleViewDestinations} onGetStarted={handleShowRegister} />
      );
    }

    if (currentView === 'destinations') {
      return (
        <Destinations onBack={handleBackToLanding} onViewArticle={handleViewBlogArticle} onGetStarted={handleShowRegister} />
      );
    }

    // Show Tools page when not logged in
    if (currentView === 'tools') {
      return (
        <ToolsPage
          onBack={handleBackToLanding}
          user={null}
          toolSlug={toolSlug}
          onSelectTool={handleSelectTool}
        />
      );
    }


    // Show Tryout page (public preview + register CTA)
    if (currentView === 'tryout') {
      return (
        <TryoutPage
          onBack={handleBackToLanding}
          onGetStarted={handleShowRegister}
          onLogin={handleShowLogin}
          initialDestination={planDestination}
        />
      );
    }

    // Show Rewards page when not logged in
    if (currentView === 'rewards') {
      return (
        <div className="min-h-screen flex flex-col bg-neutral-50">
          <RewardsPage onBack={handleBackToLanding} />
        </div>
      );
    }

    // Show Pricing page when not logged in
    if (currentView === 'pricing') {
      return (
        <div className="min-h-screen flex flex-col bg-neutral-50">
          <Pricing
            user={null}
            onBack={handleBackToLanding}
            onUpgradeSuccess={() => {}}
            onShowRegister={handleShowRegister}
          />
        </div>
      );
    }

    // Show change password form if password reset is required
    if (showChangePassword) {
      return (
        <div className="min-h-screen flex flex-col bg-gradient-to-br from-primary-500 via-primary-600 to-secondary-500">
          <main className="flex-1 flex items-center justify-center px-4 py-8">
            <ChangePassword onPasswordChanged={handlePasswordChanged} />
          </main>
        </div>
      );
    }

    // The emailed sign-in link. Must be reachable with no session, since
    // establishing one is precisely what it does.
    if (currentView === 'magic-link') {
      return <MagicLinkLanding onSignedIn={handleMagicLinkSignedIn} />;
    }

    // A published trip, readable by anyone with the link (no auth required).
    if (currentView === 'public-trip' && publicTripToken) {
      return (
        <PublicTripView
          token={publicTripToken}
          onGetStarted={handlePlanTripAnonymous}
        />
      );
    }

    // Show sample itinerary (no auth required)
    if (currentView === 'sample' && sampleSlug) {
      return (
        <SampleItinerary
          slug={sampleSlug}
          onBack={handleBackFromSample}
          onGetStarted={handleShowRegister}
        />
      );
    }

    // Show landing page by default
    return (
      <div>
        {/* Cookie Consent Banner */}
        <CookieConsent onConsentChange={setCookieConsent} />
        {/* Retargeting Pixels (loads based on consent) */}
        <RetargetingPixels cookieConsent={cookieConsent} />

        <LandingPage
          onGetStarted={handleShowRegister}
          onLogin={handleShowLogin}
          onViewTerms={handleViewTerms}
          onViewPrivacy={handleViewPrivacy}
          onViewContact={handleViewContact}
          onViewAbout={handleViewAbout}
          onViewFaq={handleViewFaq}
          onViewHowTo={handleViewHowTo}
          onViewAffiliate={handleViewAffiliate}
          onViewSample={handleViewSample}
          onViewBlog={handleViewBlog}
          onViewPricing={handleViewPricing}
          onViewGuides={handleViewGuides}
          onViewDestinations={handleViewDestinations}
          onViewTools={handleViewTools}
          onViewTryout={handleViewTryout}
        />

        {/* Terms of Service Modal - rendered via Portal */}
        {showTermsModal && ReactDOM.createPortal(
          <div
            className="fixed inset-0 flex items-center justify-center"
            style={{ zIndex: 99999 }}
          >
            <div
              className="absolute inset-0 bg-black"
              style={{ opacity: 0.6 }}
              onClick={() => setShowTermsModal(false)}
            />
            <div
              className="relative bg-white rounded-2xl shadow-xl w-full mx-4 flex flex-col"
              style={{ maxWidth: '56rem', maxHeight: '90vh' }}
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
                <h2 className="text-xl font-bold text-gray-800">Terms of Service</h2>
                <button
                  onClick={() => setShowTermsModal(false)}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <FaTimes className="text-lg" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto px-6 py-4">
                <TermsOfService />
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Privacy Policy Modal - rendered via Portal */}
        {showPrivacyModal && ReactDOM.createPortal(
          <div
            className="fixed inset-0 flex items-center justify-center"
            style={{ zIndex: 99999 }}
          >
            <div
              className="absolute inset-0 bg-black"
              style={{ opacity: 0.6 }}
              onClick={() => setShowPrivacyModal(false)}
            />
            <div
              className="relative bg-white rounded-2xl shadow-xl w-full mx-4 flex flex-col"
              style={{ maxWidth: '56rem', maxHeight: '90vh' }}
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
                <h2 className="text-xl font-bold text-gray-800">Privacy Policy</h2>
                <button
                  onClick={() => setShowPrivacyModal(false)}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <FaTimes className="text-lg" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto px-6 py-4">
                <PrivacyPolicy />
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
    );
  }

  // Authenticated - show dashboard or itinerary views
  return (
    <>
    {/* Cookie Consent Banner */}
    <CookieConsent onConsentChange={setCookieConsent} />
    {/* Retargeting Pixels (loads based on consent) */}
    <RetargetingPixels cookieConsent={cookieConsent} />

    <Layout
      user={user}
      currentView={currentView}
      onNavigate={handleNavigate}
      onLogout={handleLogout}
    >
      {/* Offline indicator for premium users */}
      {!['free', 'explorer'].includes(user?.subscription_tier) && user?.subscription_tier && <OfflineIndicator />}

      {currentView === 'dashboard' && (
        <Dashboard
          user={user}
          onLogout={handleLogout}
          onNewItinerary={handleNewItinerary}
          onViewItinerary={handleViewItinerary}
          onRegenerateItinerary={handleRegenerateFromDashboard}
          onViewProfile={handleViewProfile}
          loadingItineraryId={loadingItineraryId}
          onViewConfiguredItineraries={handleViewConfiguredItineraries}
          onViewPricing={handleViewPricing}
        />
      )}

      {currentView === 'profile' && (
        <UserProfile
          user={user}
          onProfileUpdate={handleProfileUpdate}
          onBack={handleBackToDashboard}
          onViewPricing={handleViewPricing}
        />
      )}

      {currentView === 'account' && (
        <Account
          user={user}
          onBack={handleBackToDashboard}
          onViewPricing={handleViewPricing}
          onSubscriptionCancelled={handleSubscriptionCancelled}
          onViewTravelPoints={REWARDS_ENABLED ? () => setCurrentView('travel-points') : null}
        />
      )}

      {REWARDS_ENABLED && currentView === 'travel-points' && (
        <TravelPoints
          user={user}
          onBack={handleBackToDashboard}
          onViewPricing={handleViewPricing}
        />
      )}

      {currentView === 'rewards' && (
        <RewardsPage onBack={handleBackToDashboard} />
      )}

      {currentView === 'pricing' && (
        <Pricing
          user={user}
          onBack={handleBackToDashboard}
          onUpgradeSuccess={handleUpgradeSuccess}
        />
      )}

      {currentView === 'contact' && (
        <ContactUs />
      )}

      {currentView === 'affiliate' && (
        <AffiliateRequest />
      )}

      {currentView === 'about' && (
        <AboutUs onBack={handleBackToDashboard} />
      )}

      {currentView === 'faq' && (
        <FAQ onBack={handleBackToDashboard} />
      )}

      {currentView === 'privacy' && (
        <PrivacyPolicy />
      )}

      {currentView === 'terms' && (
        <TermsOfService />
      )}

      {currentView === 'howto' && (
        <HowTo onBack={handleBackToDashboard} onViewVideos={handleViewVideos} />
      )}

      {currentView === 'videos' && (
        <Videos onBack={handleViewHowTo} />
      )}

      {currentView === 'blog' && (
        <BlogList onBack={handleBackToDashboard} onViewArticle={handleViewBlogArticle} onGetStarted={handleShowRegister} onViewTryout={handleViewTryout} />
      )}

      {currentView === 'blog-article' && blogSlug && (
        <BlogArticle
          slug={blogSlug}
          onBack={handleViewBlog}
          onViewBlog={handleViewBlog}
          onViewTryout={handleViewTryout}
          onViewArticle={handleViewBlogArticle}
          onPlanTrip={handlePlanTripForUser}
        />
      )}

      {currentView === 'unsubscribe' && unsubscribeToken && (
        <BlogUnsubscribe token={unsubscribeToken} onBack={handleBackToDashboard} />
      )}

      {currentView === 'guides' && (
        <TravelGuides onBack={handleBackToDashboard} onViewArticle={handleViewBlogArticle} onViewDestinations={handleViewDestinations} onGetStarted={handleShowRegister} />
      )}

      {currentView === 'destinations' && (
        <Destinations onBack={handleBackToDashboard} onViewArticle={handleViewBlogArticle} onGetStarted={handleShowRegister} />
      )}

      {currentView === 'tools' && (
        <ToolsPage
          onBack={handleBackToDashboard}
          user={user}
          toolSlug={toolSlug}
          onSelectTool={handleSelectTool}
        />
      )}

      {currentView === 'configured-itineraries' && (
        <ConfiguredItineraries
          onBack={handleBackToDashboard}
          onSelectItinerary={handleSelectConfiguredItinerary}
        />
      )}

      {currentView === 'configured-itinerary-detail' && configuredSlug && (
        <ConfiguredItineraryDetail
          slug={configuredSlug}
          user={user}
          onBack={handleBackFromConfiguredDetail}
          onCloned={handleConfiguredCloned}
          onViewPricing={handleViewPricing}
        />
      )}

      {/* Redirect logged-in users from tryout to dashboard */}
      {currentView === 'tryout' && (
        <Dashboard
          user={user}
          onLogout={handleLogout}
          onNewItinerary={handleNewItinerary}
          onViewItinerary={handleViewItinerary}
          onRegenerateItinerary={handleRegenerateFromDashboard}
          onViewProfile={handleViewProfile}
          loadingItineraryId={loadingItineraryId}
        />
      )}

      {/* Redirect logged-in users from landing to dashboard */}
      {currentView === 'landing' && (
        <Dashboard
          user={user}
          onLogout={handleLogout}
          onNewItinerary={handleNewItinerary}
          onViewItinerary={handleViewItinerary}
          onRegenerateItinerary={handleRegenerateFromDashboard}
          onViewProfile={handleViewProfile}
          loadingItineraryId={loadingItineraryId}
          onViewConfiguredItineraries={handleViewConfiguredItineraries}
          onViewPricing={handleViewPricing}
        />
      )}

      {currentView === 'system' && (
        <System />
      )}

      {currentView === 'analytics' && (
        <Analytics />
      )}

      {currentView === 'feedback-review' && (
        <FeedbackReview />
      )}

      {currentView === 'docker-monitoring' && (
        <DockerMonitoring />
      )}

      {currentView === 'test-runner' && (
        <TestRunner />
      )}

      {currentView === 'new-itinerary' && (
        <div className="space-y-6">
          <button
            onClick={handleBackToDashboard}
            className="flex items-center space-x-2 text-neutral-700 hover:text-primary-600 font-medium transition-colors"
          >
            <span>←</span>
            <span>Back to Dashboard</span>
          </button>
          <ItineraryForm
            onSubmit={handleSubmitItinerary}
            loading={loading}
            initialData={formInitialData}
            subscriptionLimitError={subscriptionLimitError}
            onViewPricing={() => {
              setSubscriptionLimitError(null);
              setCurrentView('pricing');
            }}
          />
        </div>
      )}

      {currentView === 'progress' && itinerary && (
        <ItineraryProgress
          requestId={requestId}
          itinerary={itinerary}
          tripDetails={{
            start_location: itinerary.start_location,
            end_location: itinerary.end_location,
            interests: itinerary.interests
          }}
          onComplete={() => {
            setLoading(false);
          }}
          onViewItinerary={() => {
            setCurrentView('view-itinerary');
          }}
        />
      )}

      {currentView === 'view-itinerary' && !itinerary && loadingItineraryId && (
        <div className="flex flex-col items-center justify-center py-24 text-neutral-600">
          <div className="w-10 h-10 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mb-4"></div>
          <p className="font-medium">Loading your itinerary…</p>
        </div>
      )}

      {currentView === 'view-itinerary' && itinerary && (
        <div className="space-y-6">
          <button
            onClick={handleBackToDashboard}
            className="flex items-center space-x-2 text-neutral-700 hover:text-primary-600 font-medium transition-colors"
          >
            <span>←</span>
            <span>Back to Dashboard</span>
          </button>
          <ItineraryDisplay
            itinerary={itinerary}
            onReset={handleNewItinerary}
            onRegenerate={handleRegenerateItinerary}
            onChatModificationApplied={handleChatModificationApplied}
            onRefresh={handleRefreshItinerary}
            isOwner={viewingAsOwner}
            isMember={!viewingAsOwner}
            onViewPricing={handleViewPricing}
            onRefreshItinerary={handleRefreshItinerary}
            currentUser={user}
          />
        </div>
      )}

      {/* Ads disabled for now */}
      {/* <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
        <AdSense adSlot="1234567890" style={{ width: '100%', height: '250px' }} />
        <AdSense adSlot="0987654321" adFormat="horizontal" style={{ width: '100%', height: '250px' }} />
      </div> */}

      {/* Terms of Service Modal */}
      <Modal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        title="Terms of Service"
        size="large"
      >
        <TermsOfService />
      </Modal>

      {/* Privacy Policy Modal */}
      <Modal
        isOpen={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
        title="Privacy Policy"
        size="large"
      >
        <PrivacyPolicy />
      </Modal>
    </Layout>

    {/* Toast Notifications */}
    <ToastContainer toasts={toasts} removeToast={removeToast} />
  </>
  );
}

// Wrap App with VoiceProvider for global voice settings
const AppWithVoice = () => (
  <VoiceProvider>
    <App />
  </VoiceProvider>
);

export default AppWithVoice;
